import { FieldValue, SmartformTranslations } from '../../..';
import { TypeaheadOption } from './useApiTypeahead';

export enum ExtraItemName {
  INFO = '_INFO'
}
export function getMenuItem(
  label: string,
  id: ExtraItemName = ExtraItemName.INFO,
  disabled?: boolean
): TypeaheadOption<any> {
  return { label, value: '', id, disabled };
}

export function getVisibleOptions<OptionValue = FieldValue>(
  translations: SmartformTranslations | undefined,
  showAllValues: boolean,
  showAllMenuItems: boolean,
  isApiDriven: boolean,
  isFilter: boolean,
  totalCount: number | undefined,
  inputText: string,
  loading: boolean | undefined,
  selectedValueLabel: string | undefined,
  recentItems: TypeaheadOption<FieldValue>[],
  values: TypeaheadOption<OptionValue>[] | undefined = [],
  excludedValues: string[] | undefined = [],
  allowedValues: string[] | undefined = []
): {
  visibleElements: TypeaheadOption<OptionValue>[];
  providedElements: TypeaheadOption<OptionValue>[];
} {
  const withAllowedValues = allowedValues?.length
    ? (values ?? []).filter(
        (v) => allowedValues.includes(v.label) || (typeof v.value === 'string' && allowedValues.includes(v.value))
      )
    : (values ?? []);

  const withoutExcludedMenuItems = !excludedValues
    ? withAllowedValues
    : withAllowedValues.filter(
        (v) => !excludedValues.includes(v.label) && !(typeof v.value === 'string' && excludedValues.includes(v.value))
      );

  const cloned = clone(withoutExcludedMenuItems);
  const elements = showAllValues
    ? cloned
    : cloned.filter((option) => {
        if (showAllMenuItems || !isFilter || !inputText) {
          return true; // show all elements when someone clicked on arrow icon or typeahead is turned of (filter) or input text is empty
        }
        // find an dropdown option
        return textMatch(inputText, `${option.label}${option.code ? ` ${option.code}` : ''}`);
      });

  // is api driven component and totalCount is not set or there are more elements in BE
  if (isApiDriven && (typeof totalCount === 'undefined' || elements.length < totalCount)) {
    if (loading) {
      elements.push(getMenuItem(translations?.loadingLabel || 'Loading', ExtraItemName.INFO, true));
    } else if (!elements.length) {
      elements.push(
        getMenuItem(
          !inputText || recentItems.find((item) => selectedValueLabel?.includes(item.label))
            ? translations?.startTypingLabel || 'Start typing to search...'
            : translations?.noMatchesLabel
              ? translations?.noMatchesLabel({ inputText })
              : `No matches for ${inputText}`,
          ExtraItemName.INFO,
          true
        )
      );
    } else if (elements.length < (totalCount ?? 0)) {
      elements.push(
        getMenuItem(translations?.moreResultsLabel || '... Type to load more results', ExtraItemName.INFO, true)
      );
    }
    // if there is no dropdown values add "no options"
  } else if (!elements.length) {
    elements.push(getMenuItem(translations?.noOptionsLabel || 'No options', ExtraItemName.INFO, true));
  }
  return { visibleElements: elements, providedElements: withoutExcludedMenuItems };
}

function textMatch(text: string, label: string, code?: string) {
  return (
    label.toLowerCase().includes(text.toLowerCase()) || (code ? code.toLowerCase().includes(text.toLowerCase()) : false)
  );
}

function clone<T>(base: T[]): T[] {
  const newArray = [] as T[];
  for (let i = 0; i < base.length; i++) {
    newArray[i] = base[i] as T;
  }
  return newArray;
}
